import React, { useLayoutEffect, useState } from 'react';
import { Bead } from './Bead';
import { BeadSequence } from '../../constants/rosarySequences';
import { hapticFeedback, useHapticPreference } from '../../utils/hapticFeedback';

interface BeadChainProps {
  beadSequence: BeadSequence[];
  currentBeadIndex: number;
  onBeadClick: (beadIndex: number, bead: BeadSequence) => void;
  debugOffset?: number;
}

export const BeadChain: React.FC<BeadChainProps> = ({
  beadSequence,
  currentBeadIndex,
  onBeadClick,
  debugOffset = 0
}) => {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const chainRef = React.useRef<HTMLDivElement>(null);
  const touchStartY = React.useRef<number>(0);
  const touchStartTime = React.useRef<number>(0);
  const [containerHeight, setContainerHeight] = useState(0);
  const [rubberBandOffset, setRubberBandOffset] = useState(0);
  const rubberBandTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const hapticEnabled = useHapticPreference();

  // When bead changes, cancel any pending rubber band (the bead moved so no need for feedback)
  React.useEffect(() => {
    if (rubberBandTimeoutRef.current) {
      clearTimeout(rubberBandTimeoutRef.current);
      rubberBandTimeoutRef.current = null;
    }
    // Reset rubber band offset immediately when bead changes
    setRubberBandOffset(0);
  }, [currentBeadIndex]);

  // Measure container height on mount and resize
  useLayoutEffect(() => {
    const updateHeight = () => {
      if (containerRef.current) {
        setContainerHeight(containerRef.current.clientHeight);
      }
    };
    updateHeight();
    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, []);

  // Get actual bead height based on bead type
  // These must match the Tailwind classes in Bead.tsx!
  const getBeadHeight = (beadType: string): number => {
    switch (beadType) {
      case 'cross': return 56;      // h-14 = 56px
      case 'large-bead': return 44; // h-11 = 44px (Our Father beads - larger)
      case 'small-bead': return 32; // h-8 = 32px (Hail Mary beads - smaller)
      case 'medal': return 40;      // h-10 = 40px
      default: return 32;
    }
  };

  // Calculate where a bead's center is, measured from the BOTTOM of the chain
  // (With flex-col-reverse, bead 0 is at the bottom, so bead 0's center is near 0)
  const getBeadPositionFromBottom = (beadIndex: number): number => {
    const beadSpacing = 12;
    const adjustmentPerBead = 3; // Fine-tuning to prevent upward drift
    let position = 0;

    // Add up all beads below this one (bead 0 to beadIndex-1)
    for (let i = 0; i < beadIndex; i++) {
      position += getBeadHeight(beadSequence[i].type) + beadSpacing + adjustmentPerBead;
    }
    // Add half of the current bead to get to its center
    position += getBeadHeight(beadSequence[beadIndex].type) / 2;

    return position;
  };

  // Calculate transform offset to position current bead at the sweet spot (vertical center)
  const getTransformOffset = () => {
    // Sweet spot: vertical center of the container
    const sweetSpot = containerHeight / 2;

    // With flex-col-reverse and bottom-0:
    // - The chain's BOTTOM edge is anchored at the container's bottom
    // - Bead 0 (cross) is at the bottom of the chain
    // - Last bead (medal) is at the top of the chain
    //
    // At translateY(0):
    // - Bead 0's center is at (containerHeight - half of bead 0's height) from top
    // - This is near the bottom of the container
    //
    // To bring bead N to the sweet spot:
    // - Bead N is at beadPositionFromBottom pixels above the chain's bottom
    // - At translateY(0), bead N is at (containerHeight - beadPositionFromBottom) from top
    // - We want it at sweetSpot from top
    // - So we need to move the chain DOWN by: (containerHeight - beadPositionFromBottom) - sweetSpot
    //   = containerHeight - beadPositionFromBottom - sweetSpot
    //   = sweetSpot - beadPositionFromBottom (since sweetSpot = containerHeight/2)
    //
    // Wait, let me think again:
    // - Positive translateY moves the chain DOWN on screen
    // - When chain moves DOWN, beads that were higher move DOWN into view
    // - Bead 0 is near bottom, needs small offset
    // - Last bead is near top, needs large positive offset to push chain down

    const beadPositionFromBottom = getBeadPositionFromBottom(currentBeadIndex);

    // How far is this bead from the sweet spot at translateY(0)?
    // Bead is at (containerHeight - beadPositionFromBottom) from top
    // Sweet spot is at sweetSpot from top
    // Difference = (containerHeight - beadPositionFromBottom) - sweetSpot
    //            = containerHeight - beadPositionFromBottom - containerHeight/2
    //            = containerHeight/2 - beadPositionFromBottom
    //            = sweetSpot - beadPositionFromBottom
    //
    // If this is positive, bead is below sweet spot, need to move chain UP (negative)
    // If this is negative, bead is above sweet spot, need to move chain DOWN (positive)
    // So offset = -(sweetSpot - beadPositionFromBottom) = beadPositionFromBottom - sweetSpot

    const offset = beadPositionFromBottom - sweetSpot;

    return offset + debugOffset + rubberBandOffset;
  };

  // Trigger rubber band effect - briefly offset then spring back
  // This is called immediately on swipe, then springs back after delay
  // If the bead actually changes, the useEffect above will cancel this
  const triggerRubberBand = (direction: 'down' | 'up') => {
    // Clear any existing timeout
    if (rubberBandTimeoutRef.current) {
      clearTimeout(rubberBandTimeoutRef.current);
    }

    const offsetAmount = direction === 'down' ? 10 : -10;
    setRubberBandOffset(offsetAmount);

    // Spring back after a brief moment
    rubberBandTimeoutRef.current = setTimeout(() => {
      setRubberBandOffset(0);
      rubberBandTimeoutRef.current = null;
    }, 120);
  };

  // Handle swipe down to advance to next bead
  const handleTouchStart = (e: React.TouchEvent) => {
    // Don't prevent default here - let touchAction CSS handle it
    touchStartY.current = e.touches[0].clientY;
    touchStartTime.current = Date.now();
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    const touchEndY = e.changedTouches[0].clientY;
    const touchEndTime = Date.now();
    const deltaY = touchEndY - touchStartY.current;
    const deltaTime = touchEndTime - touchStartTime.current;

    // Check for downward swipe (deltaY > 0) to move forward
    if (deltaY > 50 && deltaTime < 500) {
      // Prevent default only when we're actually handling the swipe
      e.preventDefault();

      // Trigger rubber band immediately - will be cancelled if bead actually changes
      triggerRubberBand('down');

      // Trigger haptic feedback on swipe
      if (hapticEnabled) {
        hapticFeedback.beadTransition();
      }

      if (currentBeadIndex < beadSequence.length - 1) {
        // Normal forward - go to next bead
        const nextIndex = currentBeadIndex + 1;
        const nextBead = beadSequence[nextIndex];
        if (nextBead) {
          onBeadClick(nextIndex, nextBead);
        }
      } else {
        // At last bead - still notify parent (for multi-swipe on final medal)
        // Send current index + 1 to indicate "forward swipe" intent
        const currentBead = beadSequence[currentBeadIndex];
        onBeadClick(currentBeadIndex + 1, currentBead);
      }
    }
    // Check for upward swipe (deltaY < 0) to move backward
    else if (deltaY < -50 && deltaTime < 500) {
      // Prevent default only when we're actually handling the swipe
      e.preventDefault();

      // Trigger rubber band immediately - will be cancelled if bead actually changes
      triggerRubberBand('up');

      // Trigger haptic feedback on swipe
      if (hapticEnabled) {
        hapticFeedback.beadTransition();
      }

      if (currentBeadIndex > 0) {
        // Normal backward - go to previous bead
        const prevIndex = currentBeadIndex - 1;
        const prevBead = beadSequence[prevIndex];
        if (prevBead) {
          onBeadClick(prevIndex, prevBead);
        }
      } else {
        // At first bead - still notify parent (for potential multi-swipe scenarios)
        // Send current index - 1 to indicate "backward swipe" intent
        const currentBead = beadSequence[currentBeadIndex];
        onBeadClick(currentBeadIndex - 1, currentBead);
      }
    }
  };

  // Handle bead click - no scroll management needed with transforms
  const handleBeadClick = (index: number, bead: any) => {
    onBeadClick(index, bead);
  };

  return (
    <div
      ref={containerRef}
      className="relative h-full overflow-hidden w-full"
      style={{ touchAction: 'none' }}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* Bead chain with transform positioning - bead 0 (cross) at bottom, last bead (medal) at top */}
      <div
        ref={chainRef}
        className="flex flex-col-reverse items-center space-y-reverse space-y-3 absolute bottom-0 left-1/2 -translate-x-1/2 rosary-gentle-movement"
        style={{
          transform: `translateX(-50%) translateY(${getTransformOffset()}px)`
        }}
      >
        {beadSequence.map((bead, index) => (
          <div key={bead.id} data-bead-index={index}>
            <Bead
              bead={bead}
              index={index}
              totalBeads={beadSequence.length}
              isActive={index === currentBeadIndex}
              isPast={index < currentBeadIndex}
              onClick={() => handleBeadClick(index, bead)}
            />
          </div>
        ))}
      </div>
    </div>
  );
};